import { getUrlParams } from '@openeducation/hr-js-utils';
import { usePurchaserId } from '@openeducation/hr-rn-cognito-providers';
import {
  HRHeading,
  HRLicensesList,
  HRLicenseHistory,
  HRAssignReassignLicenseModal,
  useHRPortalTranslation,
  useAssignReassignLicense,
  useStudentLicensePreview,
  useLastSyncText,
  useNUStudentCourses,
  NUStudentCourses,
  NUActivitiesOverview,
  flex,
} from '@openeducation/hr-rn-components';
import {
  useGetStudentLicenseHistory,
  useNextuActivityIntervals,
  useNextuActivitiesOverview,
} from '@openeducation/hr-rn-graphql-providers';
import {
  preferredLanguageAtom,
  nextUSimplifiedAtom,
} from '@openeducation/hr-rn-jotai-atoms';
import { StudentsListField } from '@openeducation/hr-rn-shared-types';
import type { StudentsListItemData } from '@openeducation/hr-rn-shared-types';
import { styled, css } from '@openeducation/hr-rn-themes';
import { useAtomValue } from 'jotai';
import React, { useCallback } from 'react';
import { View } from 'react-native';

import NUStatisticsOfActivities from '../../components/statistics-of-activities/nu-statistics-of-activities';
import { useStudentsListProps } from '../../hooks/use-students-list-props';
import { NUPath } from '../../router';

const NUStudentScreen = () => {
  const { t } = useHRPortalTranslation();
  const studentsListProps = useStudentsListProps(NUPath.Student);
  const preferredLanguage = useAtomValue(preferredLanguageAtom);
  const purchaserId = usePurchaserId();
  const { id: contactId } = getUrlParams();
  const { lastSyncText } = useLastSyncText();
  const {
    studentLicense,
    isLoading: isLicenseLoading,
    refetchListData,
  } = useStudentLicensePreview(contactId);
  const {
    loading: activitiesOverviewLoading,
    data: activitiesOverviewData,
    isError: activitiesOverviewError,
  } = useNextuActivitiesOverview({
    purchaserId,
    contactId,
  });
  const { data: licenseHistoryData, loading: isLicenseHistoryLoading } =
    useGetStudentLicenseHistory({ purchaserId, contactId });
  const studentName = studentLicense
    ? `${studentLicense?.firstName} ${studentLicense?.lastName}`
    : '';
  const hasValidLicense = isLicenseLoading || studentLicense;
  const studentCoursesProps = useNUStudentCourses({ contactId });
  useNextuActivityIntervals({ purchaserId, contactId });
  const { openAssignReassign, modalAssignReassignLicenseProps } =
    useAssignReassignLicense();

  const onAssignReassign = useCallback(
    (license: StudentsListItemData) => {
      openAssignReassign(license);
    },
    [openAssignReassign]
  );
  const isNextUSimplified = useAtomValue(nextUSimplifiedAtom);
  const licenseFields = isNextUSimplified
    ? [
        StudentsListField.NAME,
        StudentsListField.EMAIL,
        StudentsListField.STATUS,
        StudentsListField.LAST_ACTIVITY,
        StudentsListField.EXPIRATION,
        StudentsListField.ASSIGN_REASSIGN,
      ]
    : [
        StudentsListField.NAME,
        StudentsListField.EMAIL,
        StudentsListField.PLAN_TYPE,
        StudentsListField.STATUS,
        StudentsListField.LAST_ACTIVITY,
        StudentsListField.EXPIRATION,
        StudentsListField.ASSIGN_REASSIGN,
      ];
  return (
    <>
      <HRHeading
        title={studentName}
        description={`${t('student.heading.description')}. ${lastSyncText}`}
        studentsListProps={studentsListProps}
      />
      {hasValidLicense && (
        <>
          <NULicensesList
            data={[studentLicense]}
            isLoading={isLicenseLoading}
            refetchListData={refetchListData}
            isLicensePreview
            fields={licenseFields}
            onAssignReassign={onAssignReassign}
            disabledAssignFlow={isNextUSimplified}
          />
          <HRAssignReassignLicenseModal {...modalAssignReassignLicenseProps} />
        </>
      )}
      {!activitiesOverviewError && (
        <NUActivitiesOverview
          isLoading={activitiesOverviewLoading}
          stats={activitiesOverviewData}
          title={t('student.activitiesOverview.title')}
          subtitle={
            studentName &&
            t('student.activitiesOverview.subtitle', {
              name: studentName,
            })
          }
          isStudentView
        />
      )}
      <NUStudentCourses {...studentCoursesProps} />
      <View style={[flex('flex-row', 'flex-1')]}>
        <NUStatisticsOfActivities contactId={contactId} />
        {hasValidLicense && (
          <NULicenseHistory
            data={licenseHistoryData}
            activeLicense={studentLicense}
            isLoading={isLicenseHistoryLoading}
            refetchListData={refetchListData}
            language={preferredLanguage}
            onAssignReassign={onAssignReassign}
          />
        )}
      </View>
    </>
  );
};

const borderStyle = css`
  border-width: 1px;
  border-color: ${({ theme }) => theme.color.border};
`;

const NULicensesList = styled(HRLicensesList)`
  ${borderStyle}
`;

const NULicenseHistory = styled(HRLicenseHistory)`
  ${borderStyle}
`;

export default NUStudentScreen;
